// Splits the HTML description saved by the admin panel's editor into the
// section cards the course page is designed around.
//
//   <p>…</p>                      -> intro card ("About This Course")
//   <h2>Why Choose …</h2><ul>…    -> one card per H1/H2 heading
//   <h2>What You'll Learn</h2>    -> list items become chips
//   <h2>FAQs</h2><h3>Q</h3><p>A   -> click-to-open accordion

const FAQ_TITLE = /\b(faqs?|frequently asked)\b/i;
const LEARN_TITLE = /learn/i;
const LIST_TAGS = ['UL', 'OL'];

// Admin content is trusted, but drop anything that can run script anyway.
function sanitize(body) {
  body
    .querySelectorAll('script, style, iframe, object, embed, link, meta')
    .forEach((node) => node.remove());
  body.querySelectorAll('*').forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      const isHandler = /^on/i.test(attr.name);
      const isScriptUrl =
        /^(href|src|xlink:href)$/i.test(attr.name) &&
        /^\s*javascript:/i.test(attr.value);
      if (isHandler || isScriptUrl) el.removeAttribute(attr.name);
    });
  });
}

const nodeToHtml = (node) =>
  node.nodeType === 1
    ? node.outerHTML
    : node.textContent.trim()
      ? `<p>${node.textContent.trim()}</p>`
      : '';

const nodesToHtml = (nodes) => nodes.map(nodeToHtml).join('');
const textOf = (node) => (node.textContent || '').replace(/\s+/g, ' ').trim();

// A question is a H3–H6, or a paragraph that is entirely bold.
function isQuestion(node) {
  if (node.nodeType !== 1) return false;
  if (/^H[3-6]$/.test(node.tagName)) return true;
  if (node.tagName !== 'P') return false;
  const bold = node.querySelector('strong, b');
  return !!bold && textOf(bold) === textOf(node) && textOf(node) !== '';
}

function buildFaqs(nodes) {
  const faqs = [];
  nodes.forEach((node) => {
    if (isQuestion(node)) {
      faqs.push({ question: textOf(node), answer: '' });
    } else if (faqs.length) {
      const last = faqs[faqs.length - 1];
      const text = textOf(node);
      if (text) last.answer = last.answer ? `${last.answer} ${text}` : text;
    }
  });
  return faqs.filter((faq) => faq.question && faq.answer);
}

function buildSection(title, nodes) {
  const section = { title, html: '', faqs: [], chips: [] };

  if (title && FAQ_TITLE.test(title)) {
    section.faqs = buildFaqs(nodes);
    if (!section.faqs.length) section.html = nodesToHtml(nodes);
    return section;
  }

  if (title && LEARN_TITLE.test(title)) {
    const lists = nodes.filter((n) => n.nodeType === 1 && LIST_TAGS.includes(n.tagName));
    if (lists.length) {
      section.chips = lists.flatMap((list) =>
        Array.from(list.querySelectorAll('li')).map(textOf).filter(Boolean)
      );
      section.html = nodesToHtml(nodes.filter((n) => !lists.includes(n)));
      return section;
    }
  }

  section.html = nodesToHtml(nodes);
  return section;
}

export function splitDescriptionHtml(html) {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  sanitize(doc.body);

  const groups = [{ title: null, nodes: [] }];
  Array.from(doc.body.childNodes).forEach((node) => {
    if (node.nodeType === 1 && /^H[12]$/.test(node.tagName)) {
      groups.push({ title: textOf(node), nodes: [] });
    } else if (node.nodeType !== 3 || node.textContent.trim()) {
      groups[groups.length - 1].nodes.push(node);
    }
  });

  return groups
    .filter((group) => group.title || group.nodes.length)
    .map((group) => buildSection(group.title, group.nodes))
    .filter((s) => s.html || s.faqs.length || s.chips.length);
}
